import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useToast } from '../context/ToastContext';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
  siteId: string | null;
  onUpgradeSuccess: () => void;
  planType: string;
  unlockedSiteId?: string | null;
  workspaceId?: string | null;
}

export default function UpgradeModal({ isOpen, onClose, token, siteId, onUpgradeSuccess, planType, unlockedSiteId, workspaceId }: UpgradeModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [waitingForPayment, setWaitingForPayment] = useState<'PERSONAL' | 'UNLIMITED' | false>(false);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (waitingForPayment && token) {
      interval = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/user/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.planType === waitingForPayment) {
            clearInterval(interval);
            setWaitingForPayment(false);
            onUpgradeSuccess();
            onClose();
          }
        } catch (e) {
          console.error(e);
        }
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [waitingForPayment, token, onUpgradeSuccess, onClose]);

  const [isYearly, setIsYearly] = useState(true);

  const handleCheckout = async (plan: 'PERSONAL' | 'UNLIMITED') => {
    if (!token) return;
    setLoading(true);

    // If already on a paid plan and trying to upgrade, route to portal to prevent duplicate subscriptions
    if (planType === 'PERSONAL' && plan === 'UNLIMITED') {
      try {
        const res = await fetch(`${API_BASE_URL}/api/lemonsqueezy/portal?workspaceId=${workspaceId}`, { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (data.url) {
          window.open(data.url, '_blank');
          onClose();
        } else {
          showToast("Could not open portal.", "error");
        }
      } catch (err) {
        showToast("Error opening portal", "error");
      } finally {
        setLoading(false);
      }
      return;
    }

    let variantId = 0;
    if (plan === 'PERSONAL') {
      variantId = isYearly ? 2001166 : 2001122;
    } else {
      variantId = isYearly ? 2001155 : 2001159;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/lemonsqueezy/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ variantId, siteId, workspaceId })
      });
      const data = await res.json();

      if (data.url) {
        window.open(data.url, '_blank');
        setWaitingForPayment(plan);
      } else {
        showToast("Failed to initialize checkout.", "error");
      }
    } catch (err) {
      showToast("Error starting checkout", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let previousActiveElement: HTMLElement | null = null;
    
    if (isOpen) {
      previousActiveElement = document.activeElement as HTMLElement;
      const closeBtn = document.getElementById('close-modal-btn');
      if (closeBtn) closeBtn.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      
      if (e.key === 'Tab') {
        const modal = document.getElementById('upgrade-modal-content');
        if (!modal) return;
        
        const focusableElements = modal.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length === 0) return;
        
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (previousActiveElement && isOpen) {
        previousActiveElement.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen || !token) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div 
        id="upgrade-modal-content"
        role="dialog" 
        aria-modal="true" 
        aria-labelledby="modal-title"
        className="bg-[var(--background1)] border border-[var(--border1)] rounded-[8px] p-6 max-w-2xl w-full shadow-2xl relative"
      >
        <button 
          id="close-modal-btn"
          aria-label="Close upgrade modal"
          onClick={onClose} 
          className="absolute top-4 right-4 text-[var(--text3)] hover:text-[var(--text1)] focus:outline-none focus:ring-2 focus:ring-[var(--blueBorder)]"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="flex justify-between items-end mb-6">
          <div>
            <h2 id="modal-title" className="text-[20px] md:text-[21px] font-bold text-[var(--text1)] mb-2">Upgrade Your Plan</h2>
            <p className="text-[13px] md:text-[14px] text-[var(--text2)] leading-relaxed">
              {planType === 'PERSONAL' && unlockedSiteId && unlockedSiteId !== siteId
                ? "You have a Personal plan active on another site. Upgrade to Unlimited to unlock full inspections across all sites in this workspace."
                : planType === 'PERSONAL'
                ? "You are currently on the Personal plan. Upgrade to Unlimited to unlock full inspections across all sites in this workspace."
                : "You have reached your free plan limit. Choose a subscription to unlock full inspections."}
            </p>
          </div>
          
          <div className="flex flex-col items-end">
            <div className="flex bg-[var(--background2)] p-1 rounded border border-[var(--border1)]">
              <button 
                onClick={() => setIsYearly(false)}
                className={`px-3 py-1.5 text-xs font-medium rounded ${!isYearly ? 'bg-[var(--background3)] text-[var(--text1)] shadow' : 'text-[var(--text3)] hover:text-[var(--text1)]'}`}
              >
                Monthly
              </button>
              <button 
                onClick={() => setIsYearly(true)}
                className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1 ${isYearly ? 'bg-[var(--background3)] text-[var(--text1)] shadow' : 'text-[var(--text3)] hover:text-[var(--text1)]'}`}
              >
                Yearly <span className="text-[9px] md:text-[10px] bg-green-500/20 text-green-400 px-1 rounded">Save 20%</span>
              </button>
            </div>
            {planType === 'PERSONAL' && (
              <div className="text-[10px] md:text-[11px] text-[var(--text3)] mt-2 max-w-[180px] text-right">
                Use <span className="text-[var(--text2)] font-medium">Manage Subscription</span> in the Settings tab to switch billing cycle.
              </div>
            )}
          </div>
        </div>

        {waitingForPayment ? (
          <div className="flex flex-col items-center justify-center py-12">
            <svg className="animate-spin w-10 h-10 text-[var(--blueText)] mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <h3 className="text-[var(--text1)] text-lg font-bold mb-2">Waiting for payment...</h3>
            <p className="text-[var(--text3)] text-sm text-center max-w-sm mb-6">
              Please complete the checkout in the new tab that was opened. This window will automatically close once your payment is confirmed!
            </p>
            <button
              onClick={() => setWaitingForPayment(false)}
              className="text-[var(--text3)] hover:text-[var(--text1)] underline text-sm transition"
            >
              Cancel Checkout
            </button>
          </div>
        ) : (
          <div className="flex gap-4 mb-4">
            <div className="flex-1 p-5 rounded-lg border border-[var(--border1)] bg-[var(--background2)] flex flex-col">
              <h3 className="text-lg font-bold text-[var(--text1)] mb-1">Personal</h3>
              <p className="text-[var(--text3)] text-sm mb-4">Perfect for single site owners</p>
              <div className="text-2xl font-bold text-[var(--text1)] mb-4">
                {isYearly ? '$86' : '$9'} <span className="text-sm font-normal text-[var(--text3)]">/ {isYearly ? 'year' : 'month'}</span>
              </div>
              <ul className="text-sm text-[var(--text2)] space-y-2 mb-6 flex-1">
                <li>✓ Unlimited items scanned</li>
                <li>✓ Unlimited collections</li>
                <li>✓ 1 Webflow Site inside this workspace</li>
              </ul>
              <button
                onClick={() => handleCheckout('PERSONAL')}
                disabled={loading || planType === 'PERSONAL'}
                className="w-full bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] font-medium py-2 px-4 rounded-[4px] transition disabled:opacity-50"
              >
                {loading ? 'Starting Checkout...' : planType === 'PERSONAL' ? 'Current Plan' : 'Select Personal'}
              </button>
            </div>

            <div className="flex-1 p-5 rounded-lg border border-[#186ade] bg-[var(--actionPrimaryBackground)]/10 flex flex-col relative">
              <div className="absolute top-0 right-0 bg-[var(--actionPrimaryBackground)] text-[var(--actionPrimaryText)] text-[10px] md:text-[11px] font-bold px-2 py-1 rounded-bl-lg rounded-tr-md uppercase tracking-wider">
                Best Value
              </div>
              <h3 className="text-lg font-bold text-[var(--text1)] mb-1">Unlimited</h3>
              <p className="text-[var(--text3)] text-sm mb-4">For agencies and freelancers</p>
              <div className="text-2xl font-bold text-[var(--text1)] mb-4">
                {isYearly ? '$278' : '$29'} <span className="text-sm font-normal text-[var(--text3)]">/ {isYearly ? 'year' : 'month'}</span>
              </div>
              <ul className="text-sm text-[var(--text2)] space-y-2 mb-6 flex-1">
                <li>✓ Unlimited items scanned</li>
                <li>✓ Unlimited collections</li>
                <li>✓ Unlimited Webflow Sites in workspace</li>
              </ul>
              <button
                onClick={() => handleCheckout('UNLIMITED')}
                disabled={loading || planType === 'UNLIMITED'}
                className="w-full bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] font-medium py-2 px-4 rounded-[4px] transition disabled:opacity-50"
              >
                {loading ? 'Starting Checkout...' : planType === 'PERSONAL' ? 'Upgrade to Unlimited' : planType === 'UNLIMITED' ? 'Current Plan' : 'Select Unlimited'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
